package one.devsbox.www.ui.apps

import androidx.compose.animation.Crossfade
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.ui.draw.clip
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import compose.assets.Svg
import compose.assets.svg.LayersOutline
import one.devsbox.shared.ads.SharedIO
import one.devsbox.shared.devices.ApplicationType
import one.devsbox.www.ui.widgets.*
import one.devsbox.www.www_portal.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.compose.navigation.navTo_AppWheelDUI
import tk.mallumo.utils.DataState

@Composable
@ComposableNavNode
fun ListingAppsUI() {
    with(ListingAppsCtx.get()) {
        ContentListingAppsUI()
    }
}

@Composable
context(ctx: ListingAppsCtx)
private fun ContentListingAppsUI() {
    Box(modifier = Modifier.fillMaxSize()) {
        Crossfade(targetState = ctx.vm.state) { state ->
            when (state) {
                is DataState.Idle -> Unit
                is DataState.Loading -> FullscreenLoadingUI()
                is DataState.Error -> {
                    FullscreenErrorUI(
                        message = state.message,
                        onBack = { ctx.action.back() },
                        onReload = { ctx.action.reload() }
                    )
                }

                is DataState.Result -> Box(modifier = Modifier.fillMaxSize()) {
                    val resultData = state.nnvl
                    if (resultData.count == 0) {
                        CategorySplashUI(
                            icon = Svg.LayersOutline,
                            title = Res.string.ui_target_applications,
                            explanation = Res.string.ui_company_splash_desc, // reuse company splash description as appropriate
                            actions = ctx.vm.contentActions
                        )
                    } else {
                        SuccessUI(resultData)
                        ContentActions(
                            contentActions = ctx.vm.contentActions,
                            colorInactive = ctx.colorSecondaryContainer,
                            colorActive = ctx.colorPrimaryContainer
                        )
                    }
                }
            }
        }

        if (ctx.vm.showAppPickerDialog) {
            AlertDialog(
                onDismissRequest = { ctx.vm.showAppPickerDialog = false },
                title = {
                    Text(
                        text = "Application TYPE",
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold
                    )
                },
                confirmButton = {},
                dismissButton = {
                    TextButton(onClick = { ctx.vm.showAppPickerDialog = false }) {
                        Text("Cancel")
                    }
                },
                text = {
                    val scrollState = rememberScrollState()
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .heightIn(max = 300.dp)
                            .verticalScroll(scrollState),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        val eligibleApps = ctx.vm.wwwApps.filter { it.type != 8 }
                        eligibleApps.forEach { app ->
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .clickable {
                                        ctx.action.createNew(app.id)
                                    }
                                    .border(
                                        width = 1.dp,
                                        color = MaterialTheme.colorScheme.outlineVariant,
                                        shape = RoundedCornerShape(4.dp)
                                    )
                                    .padding(12.dp)
                            ) {
                                Text(
                                    text = app.name.uppercase(),
                                    style = MaterialTheme.typography.bodyMedium,
                                    fontWeight = FontWeight.Bold
                                )
                            }
                        }
                    }
                }
            )
        }
    }
}

@Composable
context(ctx: ListingAppsCtx)
private fun SuccessUI(data: SharedIO.AppInstancies) {
    val filteredItems = data.items.filter {
        it.name.contains(ctx.vm.search.text, ignoreCase = true) ||
                it.companyName.contains(ctx.vm.search.text, ignoreCase = true)
    }.sortedByDescending { it.id }

    Column(modifier = Modifier.fillMaxSize().padding(16.dp)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            SearchBox(
                state = ctx.vm.search,
                hint = stringResource(Res.string.ui_company_listing_search_placeholder), // reuse search placeholder
                modifier = Modifier.weight(1f),
            )
        }

        Spacer(Modifier.height(16.dp))

        val showingStart = if (filteredItems.isEmpty()) 0 else 1
        val showingEnd = filteredItems.size
        val totalCount = data.count

        Text(
            text = stringResource(
                Res.string.ui_apps_listing_showing,
                showingStart,
                showingEnd,
                totalCount
            ),
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.6f),
            letterSpacing = 1.sp,
            modifier = Modifier.padding(bottom = 16.dp)
        )

        if (filteredItems.isEmpty()) {
            Box(
                modifier = Modifier.fillMaxWidth().weight(1f),
                contentAlignment = Alignment.Center
            ) {
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.Center
                ) {
                    Icon(
                        imageVector = Svg.LayersOutline,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.4f),
                        modifier = Modifier.size(48.dp)
                    )
                    Spacer(Modifier.height(16.dp))
                    Text(
                        text = stringResource(Res.string.ui_apps_listing_no_apps_found),
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.6f)
                    )
                }
            }
        } else {
            // Table Header
            Row(
                modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp),
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                LabelField(text = stringResource(Res.string.ui_apps_listing_header_instance))
                if (ctx.me.info.adminMode) {
                    LabelField(text = stringResource(Res.string.ui_apps_listing_header_type_company))
                } else {
                    LabelField(text = stringResource(Res.string.ui_devices_listing_header_type))
                }
            }
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)

            LazyColumn(
                modifier = Modifier.weight(1f).fillMaxWidth(),
                contentPadding = PaddingValues(bottom = 250.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                item {
                    Spacer(Modifier.height(8.dp))
                }

                items(filteredItems) { appInstance ->
                    AppInstanceItem(appInstance) {
                        ctx.action.openDetail(appInstance)
                    }
                }
                if (data.items.size < data.count) {
                    item {
                        FooterSection()
                    }
                }
            }
        }
    }
}

@Composable
context(ctx: ListingAppsCtx)
private fun AppInstanceItem(appInstance: SharedIO.AppInstanceSimple, onClick: () -> Unit) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .border(
                width = 1.dp,
                color = MaterialTheme.colorScheme.outlineVariant,
                shape = RoundedCornerShape(4.dp)
            )
            .clip(RoundedCornerShape(4.dp))
            .clickable(onClick = onClick)
            .background(Color.Transparent)
            .padding(horizontal = 16.dp, vertical = 16.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Text(
                    text = appInstance.name.uppercase(),
                    style = MaterialTheme.typography.bodyLarge,
                    fontWeight = FontWeight.Bold
                )
            }

            val appType = ApplicationType[appInstance.app.type]
            val typeLabel =
                if (appType != null) stringResource(appType.label) else stringResource(Res.string.ui_apps_listing_unknown)

            Column(
                horizontalAlignment = Alignment.End,
                verticalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                Text(
                    text = typeLabel,
                    style = MaterialTheme.typography.bodyMedium,
                    fontWeight = FontWeight.SemiBold,
                    color = MaterialTheme.colorScheme.primary
                )
                if (ctx.me.info.adminMode) {
                    Text(
                        text = appInstance.companyName,
                        style = MaterialTheme.typography.labelMedium,
                        color = MaterialTheme.colorScheme.secondary
                    )
                }
            }
        }
    }
}

@Composable
context(ctx: ListingAppsCtx)
private fun FooterSection() {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 32.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        if (ctx.vm.loadingMore) {
            CircularProgressIndicator(modifier = Modifier.size(24.dp))
        } else {
            OutlinedButton(
                onClick = { ctx.vm.action.loadMore() },
                shape = RoundedCornerShape(4.dp),
                colors = ButtonDefaults.outlinedButtonColors(
                    contentColor = MaterialTheme.colorScheme.primary
                ),
                border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
                modifier = Modifier.width(200.dp).height(48.dp)
            ) {
                Text(
                    stringResource(Res.string.ui_company_listing_btn_load_more),
                    fontWeight = FontWeight.Bold
                )
            }
        }
    }
}
